import { LayerT } from './layers'
import { FeaturesT } from './utils/types'
import { Box, Button } from '@chakra-ui/react'
import html2canvas from 'html2canvas'
import _ from 'lodash'
import Legend from './Legend'
import MapContainer from './MapContainer'
import { Select } from 'components'

const MapSection = ({
  features,
  selectedLayer,
  handleLegendClick,
  selectCountry,
  mapRef,
  width,
  disease,
  mapLayers,
  setLegendType,
  legendType,
  exportTitle,
}: {
  features: FeaturesT
  selectedLayer: LayerT
  handleLegendClick: (newLayer: LayerT) => void
  selectCountry: (country: string) => void
  mapRef: any
  width?: string
  disease?: string
  mapLayers?: string[]
  setLegendType?: React.Dispatch<React.SetStateAction<string>>
  legendType?: string
  exportTitle?: string
}) => {
  const isBotulism = disease === 'Botulism'
  const exportToImage = async () => {
    const style = document.createElement('style')
    document.head.appendChild(style)
    style.sheet?.insertRule(
      'body > div:last-child img { display: inline-block; }'
    )
    style.sheet?.insertRule('#exportButtons { display: none !important; }')

    const pdfContent = document.getElementById('divToPrint') as HTMLElement
    const svgElements = document.body.querySelectorAll('svg')
    const originalDimensions = Array.from(svgElements).map((svg) => ({
      element: svg,
      width: svg.style.width,
      height: svg.style.height,
    }))

    svgElements.forEach(function (item) {
      item.setAttribute('width', String(item.getBoundingClientRect().width))
      item.setAttribute('height', String(item.getBoundingClientRect().height))
      item.style.width = ''
      item.style.height = ''
    })

    try {
      const canvas = await html2canvas(pdfContent, {
        scale: 2,
        logging: true,
      })
      const imgData = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      link.href = imgData
      link.download = `${disease} ${exportTitle || 'Epi Map Export'} - ${new Date().toLocaleDateString()}.png`
      link.click()
    } finally {
      document.head.removeChild(style)
      originalDimensions.forEach(({ element, width, height }) => {
        element.style.width = width
        element.style.height = height
      })
    }
  }

  const exportData = () => {
    const headers = [
      'Country',
      'Country Code',
      'Date',
      'Cases',
      'Type',
      'Date Start',
      'Date End',
      ...(isBotulism ? [] : ['Gender', 'Race', 'Age']),
      'Is Suspected',
      'Source',
    ].join(',')
    const rows: string[] = []

    mapLayers?.forEach((type) => {
      features[type as keyof typeof features]?.features.forEach((f) => {
        f.properties.allCases?.forEach((c) => {
          rows.push(
            [
              `"${c.area}"`,
              c.areaAlpha3,
              c.dateStart,
              c.value,
              _.upperFirst(type),
              c.dateStart,
              c.dateEnd,
              ...(isBotulism ? [] : [c.gender, c.race, c.airfinityAgeText]),
              c.isSuspected,
              c.sourceUrl,
            ].join(',')
          )
        })
      })
    })

    const sortedRows = rows.sort((a, b) => {
      const areaA = a.split(',')[0].toLowerCase()
      const areaB = b.split(',')[0].toLowerCase()
      return areaA.localeCompare(areaB)
    })

    const blob = new Blob([`${headers}\n${sortedRows.join('\n')}`], {
      type: 'text/csv;charset=utf-8;',
    })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${disease}_${legendType}_${new Date().toLocaleDateString()}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  return (
    <Box
      border='1px solid'
      borderColor='gray3'
      w={width || '460px'}
      // minW='300px'
      // maxW={'33vw'}
      h='100%'
      borderRadius={'10px'}
      position={'relative'}
      overflow='hidden'
    >
      <Box
        pos='absolute'
        gap={2}
        display={'flex'}
        top={2}
        right={'50%'}
        zIndex={10}
        id='exportButtons'
        transform={'translateX(50%)'}
      >
        {isBotulism && (
          <Select
            value={[{ label: legendType, value: legendType }]}
            isMulti={false}
            isSearchable={false}
            options={[
              { label: 'Cases', value: 'Cases' },
              { label: 'Types', value: 'Types' },
            ]}
            color='#000'
            controlBackground='#FFED00'
            onChange={(option: any) =>
              setLegendType && setLegendType(option.value)
            }
            fontSize={'16px'}
            height='42px'
          />
        )}
      </Box>
      <Box
        pos='absolute'
        gap={2}
        display={'flex'}
        top={2}
        right={2}
        zIndex={10}
        id='exportButtons'
      >
        <Button
          size={'xs'}
          border={'1px'}
          borderColor={'gray.200'}
          h={8}
          variant={'solid'}
          borderRadius={'sm'}
          onClick={exportToImage}
        >
          Export image
        </Button>
        <Button
          size={'xs'}
          border={'1px'}
          borderColor={'gray.200'}
          h={8}
          variant={'solid'}
          borderRadius={'sm'}
          onClick={exportData}
        >
          Export data
        </Button>
      </Box>
      <Legend
        handleClick={handleLegendClick}
        selectedLayer={selectedLayer}
        disease={disease}
        mapLayers={mapLayers}
      />
      <MapContainer
        mapRef={mapRef}
        selectedLayer={selectedLayer}
        features={features}
        selectCountry={selectCountry}
        disease={disease}
        mapLayers={mapLayers}
        legendType={legendType}
      />
    </Box>
  )
}

export default MapSection
